import type { FieldObstacle, FieldPoint, FieldRect, FieldShape, FieldSpec, FieldZone } from './field';
import type { TrackPoint } from './types';

// L2 확정 도면의 1600×6600u 월드. 마블 r18u → 엔진 r10px에 맞춰
// 좌표·길이·가속도만 환산하고 시간·각속도는 도면 값을 그대로 사용한다.
export const TOWER_SCALE = 10 / 18;
const S = TOWER_SCALE;
const DEG = Math.PI / 180;
const LOWER = 3300;
const point = (x: number, y: number): FieldPoint => ({ x: x * S, y: y * S });
const rect = (x: number, y: number, w: number, h: number): FieldRect => ({ ...point(x, y), w: w * S, h: h * S });
type UPoint = readonly [number, number];

function cubic(a: UPoint, b: UPoint, c: UPoint, d: UPoint, steps = 16): FieldPoint[] {
  return Array.from({ length: steps + 1 }, (_, index) => {
    const t = index / steps;
    const u = 1 - t;
    return point(
      u ** 3 * a[0] + 3 * u * u * t * b[0] + 3 * u * t * t * c[0] + t ** 3 * d[0],
      u ** 3 * a[1] + 3 * u * u * t * b[1] + 3 * u * t * t * c[1] + t ** 3 * d[1],
    );
  });
}
const pipe = (points: readonly FieldPoint[], width: number): FieldShape => ({ kind: 'pipe', points, width: width * S });
const disc = (x: number, y: number, r: number): FieldShape => ({ kind: 'disc', ...point(x, y), r: r * S });
const polygon = (points: readonly UPoint[]): FieldShape => ({ kind: 'poly', points: points.map(([x, y]) => point(x, y)) });

const ENTRY_SLOPE = cubic([800, 880], [650, 962], [442, 1050], [348, 1206]);
// 수정 도면의 반환 사발 바닥 출구. 예전 (448,1544)는 바닥에 포켓이 생긴다.
const WAVE_SLOPE = cubic([400, 1580], [700, 1690], [1010, 1710], [1222, 1764], 24);
const LOWER_SLOPE = cubic([1124, LOWER + 400], [962, LOWER + 548], [700, LOWER + 626], [486, LOWER + 690]);
const BOWL_EXIT = cubic([580, LOWER + 1230], [860, LOWER + 1350], [1120, LOWER + 1390], [1272, LOWER + 1462]);
export const TOWER_SAFE_LANE = cubic([1206, LOWER + 2000], [1010, LOWER + 2140], [830, LOWER + 2280], [700, LOWER + 2450]);
export const TOWER_FAST_LANE = cubic([1336, LOWER + 2000], [1408, LOWER + 2160], [1360, LOWER + 2340], [1180, LOWER + 2470]);

function waveGates(): FieldObstacle[] {
  return [9, 14, 19].flatMap((index, gateIndex) => {
    const center = WAVE_SLOPE[index];
    const next = WAVE_SLOPE[index + 1];
    const previous = WAVE_SLOPE[index - 1];
    const angle = Math.atan2(next.y - previous.y, next.x - previous.x);
    // 새 경사 통로의 양 벽에 붙인 반쪽 바. 중앙 58u는 항시 비워 둔다.
    return [-1, 1].map((side): FieldObstacle => {
      const offset = side * 72 * S;
      const cx = center.x - Math.sin(angle) * offset;
      const cy = center.y + Math.cos(angle) * offset;
      return {
        kind: 'gate', id: `tw-wave-${gateIndex + 1}-${side < 0 ? 'top' : 'bottom'}`,
        x: cx - 7 * S, y: cy - 43 * S, w: 14 * S, h: 86 * S, angle,
        schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: gateIndex * .6 + (side > 0 ? 1.2 : 0), eventPeriod: 1 },
        eventType: 'gate-frenzy',
      };
    });
  });
}

function pins(): FieldObstacle[] {
  // 도면의 맨 오른쪽 핀(1344)은 벽과 겹쳤다. 4/3열을 중앙 정렬해
  // 좌우 벽-핀 여유 48u, 핀-핀 여유 52u를 확보한다(마블 지름 36u).
  return [2400, 2530, 2660, 2790].flatMap((y, row) => (
    (row % 2 === 0 ? [968, 1072, 1176, 1280] : [1020, 1124, 1228])
      .map((x, column): FieldObstacle => ({ kind: 'pin', id: `tw-peg-${row}-${column}`, ...point(x, y), r: 26 * S }))
  ));
}

function bumpers(prefix: string, centers: readonly UPoint[], radius: number): FieldObstacle[] {
  return centers.map(([x, y], index) => ({
    kind: 'bumper', id: `tw-${prefix}-${index + 1}`, ...point(x, y), r: radius * S, restitution: .55,
  }));
}

function overshootRamp(): FieldObstacle[] {
  // 도면의 긴 호는 양 벽을 이어 통로를 완전히 막는다. 중앙의 짧은 곡면으로
  // 줄여 고속 마블은 바깥으로 튕기되, 양 끝으로 항상 빠져나갈 수 있게 한다.
  const points = cubic([804, LOWER + 530], [850, LOWER + 546], [866, LOWER + 586], [844, LOWER + 622], 4);
  return points.slice(1).map((to, index) => {
    const from = points[index];
    return {
      kind: 'bar', id: `tw-ramp-${index}`, x: (from.x + to.x) / 2, y: (from.y + to.y) / 2,
      length: Math.hypot(to.x - from.x, to.y - from.y) + 1, thickness: 8 * S,
      angle: Math.atan2(to.y - from.y, to.x - from.x),
    };
  });
}

const gravityZone = (x: number, y: number, w: number, h: number, scale: number, label: string): FieldZone => ({
  kind: 'gravity', rect: rect(x, y, w, h), gx: 0, gy: 600 * S * scale, label,
});

export const TOWER_FIELD: FieldSpec = {
  eventScheduling: 'occupancy',
  shapes: [
    { kind: 'room', ...rect(190, 120, 1220, 330), r: 150 * S },
    polygon([[250, 430], [620, 624], [980, 624], [1350, 430]]),
    pipe([point(800, 600), point(800, 880)], 360),
    pipe(ENTRY_SLOPE, 230),
    disc(330, 1400, 212),
    pipe(WAVE_SLOPE, 230),
    disc(1180, 2024, 250),
    // 상·하부 도면의 겹친 캡을 하나의 연속 샤프트로 합쳐 이음부 턱을 없앤다.
    pipe([point(1124, 2274), point(1124, LOWER + 400)], 460),
    pipe(LOWER_SLOPE, 240),
    disc(470, LOWER + 990, 288),
    pipe(BOWL_EXIT, 220),
    pipe([point(1272, LOWER + 1462), point(1272, LOWER + 2000)], 320),
    pipe(TOWER_SAFE_LANE, 130),
    pipe(TOWER_FAST_LANE, 110),
    { kind: 'ellipse', ...point(900, LOWER + 2570), rx: 452 * S, ry: 196 * S },
    // 도면의 332u 평평한 어깨 대신 116u 출구까지 양쪽 바닥이 직접 내려간다.
    polygon([[520, LOWER + 2650], [842, LOWER + 2872], [958, LOWER + 2872], [1280, LOWER + 2650]]),
    pipe([point(900, LOWER + 2860), point(900, LOWER + 3110)], 116),
  ],
  holes: [
    // 세로 샤프트의 둥근 끝 캡 아래로 빠지면 다음 경사로보다 낮은 U자 포켓에
    // 갇힌다. 우측에서 좌측 출구로 내려가는 바닥을 만들어 중력으로 굴려 보낸다.
    polygon([[1240, LOWER + 390], [1360, LOWER + 300], [1380, LOWER + 680], [1050, LOWER + 680], [1010, LOWER + 590]]),
    // 세로 샤프트 끝의 둥근 캡이 두 갈래 사이에 U자 고임을 만들지 않게
    // 분기 중앙 바닥을 지붕형으로 올린다. 낙하 마블은 양 경사로 흘러간다.
    polygon([[1265, LOWER + 1990], [1320, LOWER + 2165], [1100, LOWER + 2165]]),
  ],
  gravity: { x: 0, y: 600 * S },
  damping: .16,
  wallBounce: true,
  spawn: { kind: 'rect', ...rect(280, 156, 1040, 260), pitch: 52 * S, fill: 'center' },
  startGate: rect(602, 622, 396, 20),
  finish: { kind: 'rect', ...rect(842, LOWER + 3044, 116, 52) },
  obstacles: [
    ...bumpers('return', [[268, 1320], [404, 1372], [292, 1470]], 32),
    ...waveGates(),
    { kind: 'rotor', id: 'tw-rotor', ...point(1180, 2024), wingLength: 352 * S, thickness: 12 * S, speed: .4, hubR: 26 * S, eventType: 'rotor-reverse' },
    ...pins(),
    ...overshootRamp(),
    ...bumpers('bowl', [[352, LOWER + 856], [580, LOWER + 900], [330, LOWER + 1080], [576, LOWER + 1094], [466, LOWER + 972], [452, LOWER + 1176]], 34),
    { kind: 'paddle', id: 'tw-spin-1', ...point(1272, LOWER + 1620), length: 228 * S, thickness: 20 * S, speed: 110 * DEG, phase: 30 * DEG },
    { kind: 'paddle', id: 'tw-spin-2', ...point(1272, LOWER + 1800), length: 228 * S, thickness: 20 * S, speed: -110 * DEG, phase: -24 * DEG },
    // 위험 레인은 핀을 벽 쪽에 붙여 반대편에 마블 1개 이상의 틈을 남긴다.
    // 첫 핀의 왼쪽 벽 포켓은 원래 공 중심 여유 14px로 지름보다 좁았다.
    // 핀을 우측으로 옮겨 자연 낙하 경로를 열고 정체 임펄스 의존을 없앤다.
    { kind: 'pin', id: 'tw-fast-pin-1', ...point(1392, LOWER + 2126), r: 14 * S },
    { kind: 'pin', id: 'tw-fast-pin-2', ...point(1400, LOWER + 2244), r: 18 * S },
    // 축은 왼쪽 벽 밖에 두고 수평일 때 양 벽을 가로질러 출구를 완전히 막는다.
    // 양쪽 날개를 유지하고 반시계 방향으로 회전해 진입한 마블을 위로 밀어낸다.
    { kind: 'paddle', id: 'tw-finish-sweep', ...point(780, LOWER + 2872), length: 420 * S, thickness: 14 * S, speed: -.8, phase: .45 },
    // 두 분기가 만난 뒤 짧게 모여 회전봉으로 들어간다. 가장자리의 혼자 온 공도
    // 감지하도록 반경을 봉 전체 폭보다 넓게 잡고, 인원과 무관하게 3초 안에 연다.
    { kind: 'gate', id: 'tw-finish-merge', ...rect(440, LOWER + 2585, 920, 16), schedule: { type: 'crowd', count: 6, waitSeconds: 3, holdSeconds: 1.2, radius: 500 * S } },
  ],
  zones: [
    gravityZone(190, 620, 1220, 1654, 1, '중력 ↓ ×1.0'),
    gravityZone(894, 2274, 460, LOWER + 400 - 2274, 1.4, '파친코 낙하 · g×1.4'),
    gravityZone(170, LOWER + 400, 1280, 2472, 1.2, '중력 ↓ ×1.2'),
    { kind: 'accel', rect: rect(674, 700, 96, 100), ax: 0, ay: 240 * S },
    { kind: 'accel', rect: rect(834, 700, 96, 100), ax: 0, ay: 240 * S },
    // 수직 맵의 사발 드레인만 국소 보조. 전체 속도나 다른 맵의 중력은 바꾸지 않는다.
    { kind: 'accel', rect: rect(115, 1460, 435, 250), ax: 100 * S, ay: 0 },
    { kind: 'accel', rect: rect(180, LOWER + 1110, 510, 270), ax: 120 * S, ay: 0 },
    { kind: 'pump', rect: rect(510, LOWER + 2540, 100, 150), ax: 190 * S, ay: 45 * S, period: 1.2 },
    { kind: 'pump', rect: rect(1190, LOWER + 2540, 100, 150), ax: -190 * S, ay: 45 * S, period: 1.2, phase: .6 },
    // 닫힌 합류봉 앞의 의도된 대기는 정체 구조 임펄스 대상에서 제외한다.
    { kind: 'wait', rect: rect(440, LOWER + 2440, 920, 170), calm: 0 },
  ],
  events: [
    { type: 'gate-frenzy', duration: 3, window: [10, 16] },
    { type: 'rotor-reverse', duration: 2.5, window: [18, 26] },
    { type: 'gravity-surge', duration: 2, window: [28, 36] },
  ],
  eventZones: [
    { type: 'gate-frenzy', rect: rect(610, 1530, 535, 355) },
    { type: 'rotor-reverse', circle: { ...point(1180, 2024), r: 250 * S } },
    { type: 'gravity-surge', rect: rect(894, 2274, 460, 866) },
    { type: 'gravity-surge', rect: rect(1112, LOWER + 1462, 320, 538) },
  ],
  landmarks: [
    { ...point(1010, 730), label: '낙하 부스터' },
    { ...point(310, 1160), label: '반환 사발' },
    { ...point(850, 1510), label: '3연 파도 게이트' },
    { ...point(1180, 1730), label: '4날개 회전방' },
    { ...point(1110, 3020), label: '파친코 낙하' },
    { ...point(660, LOWER + 495), label: '오버슛 램프' },
    { ...point(390, LOWER + 654), label: '충돌 사발' },
    { ...point(1000, LOWER + 1710), label: '엇갈림 회전봉' },
    { ...point(840, LOWER + 2180), label: 'B · 넓고 안전' },
    { ...point(1450, LOWER + 2300), label: 'A · 짧고 위험' },
    { ...point(900, LOWER + 2520), label: '마지막 합류 관문' },
    { ...point(1080, LOWER + 2930), label: '막판 역전 · 회전봉' },
  ],
  stallSeconds: 3.4,
  rushAt: 68,
  maxSpeed: 700 * S,
  gravityEvent: { type: 'gravity-surge', scale: 1.5, maxSpeed: 820 * S },
  velocityWatchdog: { minSpeed: 8 * S, seconds: 1.2, impulse: 180 * S },
  guideRecovery: true,
  guideRecoveryDistance: 160,
  eventMinGapSeconds: 8,
  eventFinalGuardSeconds: 5,
};

/** 실제 통로를 따르는 추적/진행도 기준선. 상·하부 패널은 별도 순간이동 없이 연결. */
export function towerGuidePath(): TrackPoint[] {
  const track = (x: number, y: number, width = 230): TrackPoint => ({ ...point(x, y), width: width * S });
  const curve = (points: readonly FieldPoint[], width: number): TrackPoint[] => points
    .filter((_, index) => index % 4 === 0)
    .map((p) => ({ ...p, width: width * S }));
  return [
    track(800, 200, 1100), track(800, 430, 1000), track(800, 620, 360), track(800, 760, 360),
    ...curve(ENTRY_SLOPE, 230), track(330, 1340, 380), track(340, 1500, 380),
    ...curve(WAVE_SLOPE, 230), track(1220, 1890, 420), track(1180, 2070, 420),
    track(1124, 2274, 460), track(1124, 2580, 460), track(1124, 2900, 460),
    track(1124, 3200, 460), track(1124, 3500, 460),
    ...curve(LOWER_SLOPE, 240), track(450, LOWER + 900, 500), track(490, LOWER + 1140, 500),
    ...curve(BOWL_EXIT, 220), track(1272, LOWER + 1680, 320), track(1272, LOWER + 1870, 320),
    ...curve(TOWER_SAFE_LANE, 130), track(820, LOWER + 2620, 500), track(900, LOWER + 2780, 280),
    track(900, LOWER + 2940, 116), track(900, LOWER + 3070, 116),
  ];
}
